import React, { useMemo, useState } from 'react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Button } from '@/components/ui/Button/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  type AgendaBlock,
  AGENDA_CATEGORIES,
  categoryColor,
  computeEndTime,
  formatDuration,
  minutesToTime,
  timeToMinutes,
  snapMinutes,
} from './constants';

interface AgendaBlockEditorProps {
  block: AgendaBlock;
  onSave: (updated: AgendaBlock) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}

// 15-minute increments up to 8h — matches what drag-resize can produce.
const BASE_DURATIONS: number[] = [];
for (let m = 15; m <= 480; m += 15) BASE_DURATIONS.push(m);

export function AgendaBlockEditor({ block, onSave, onDelete, onClose }: AgendaBlockEditorProps) {
  const [name, setName] = useState(block.name);
  const [description, setDescription] = useState(block.description ?? '');
  const [categoryKey, setCategoryKey] = useState(block.category_key);
  const [startTime, setStartTime] = useState(block.start_time);
  const [duration, setDuration] = useState(block.duration_minutes);

  // Always include the current duration (e.g. if it was dragged to a value
  // outside the standard list).
  const durationOptions = useMemo(() => {
    const set = new Set(BASE_DURATIONS);
    if (duration > 0) set.add(duration);
    return Array.from(set)
      .sort((a, b) => a - b)
      .map((m) => ({ value: String(m), label: formatDuration(m) }));
  }, [duration]);

  const handleSave = () => {
    const safeStart = minutesToTime(snapMinutes(timeToMinutes(startTime)));
    onSave({
      ...block,
      name: name.trim() || 'Untitled',
      description: description.trim() ? description.trim() : null,
      category_key: categoryKey,
      color: categoryColor(categoryKey),
      start_time: safeStart,
      duration_minutes: duration,
      end_time: computeEndTime(safeStart, duration),
    });
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="md"
      title="Edit Agenda Item"
      footer={
        <div className="flex items-center justify-between w-full">
          <Button
            variant="ghost"
            onClick={() => onDelete(block.id)}
            leftIcon={<Trash2 className="w-4 h-4" />}
          >
            Delete
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <Input
          label="Title"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Dinner on the beach"
        />

        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">Category</label>
          <div className="flex flex-wrap gap-1.5">
            {AGENDA_CATEGORIES.map((c) => {
              const selected = categoryKey === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setCategoryKey(c.key)}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors',
                    selected
                      ? 'text-white border-transparent'
                      : 'text-muted-foreground border-border hover:bg-muted'
                  )}
                  style={selected ? { backgroundColor: c.color } : undefined}
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: selected ? 'rgba(255,255,255,0.9)' : c.color }}
                  />
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input
            type="time"
            label="Start time"
            step={900}
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
          />
          <Select
            label="Duration"
            value={String(duration)}
            onValueChange={(v) => setDuration(Number(v))}
            options={durationOptions}
          />
        </div>

        <Textarea
          label="Description (optional)"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g. Alternative venue off-site / includes 9h transfer"
        />
      </div>
    </Modal>
  );
}
